import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import torse from './../../static/images/marketing/torse.png';
import genoux from './../../static/images/marketing/genoux.png';
import cuisses from './../../static/images/marketing/cuisses.png';

import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';

const items = [
  {
    icon: torse,
    title: 'Increased surgical confidence',
    description:
      'By providing a clear, interactive 3D model of the patient’s unique anatomy and nervous system, REPLICO boosts surgical confidence and precision, helping to preserve sensory function, motor skills, and mitigate functional disorders such as speech, swallowing or incontinence.',
  },
  {
    icon: genoux,
    title: 'Improved surgical planning',
    description:
      'Surgeons can thoroughly assess critical anatomical structures, including nerves, before the procedure, enabling a more comprehensive surgical plan, optimal approach, and technique, while reducing errors, ensuring fewer complications, faster recovery and an overall improved patient experience.',
  },
  {
    icon: cuisses,
    title: 'Enhanced patient understanding',
    description:
      'These models are not only invaluable for surgeons but also provide patients with greater confidence in postoperative outcomes, leading to improved acceptance, reduced anxiety, and better-informed expectations about potential complications.',
  },
];
export default function Features() {
  const [loadedImages, setLoadedImages] = React.useState({});
  const hasAnyImageLoaded = Object.keys(loadedImages).length > 0;

  const handleImageLoad = (key) => {
    setLoadedImages((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <Container
      id="features"
      sx={{ py: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 } }}
    >
      <Box sx={{ width: { sm: '100%', md: '60%' } }}>
        {!hasAnyImageLoaded ? (
          <>
            <Skeleton variant="text" width="45%" height={36} />
            <Skeleton variant="text" width="95%" height={20} />
            <Skeleton variant="text" width="90%" height={20} />
          </>
        ) : (
          <>
            <Typography
              component="h1"
              variant="h2"
              gutterBottom
              sx={{
                color: '#333333',
                fontSize: { xs: '1.6rem', sm: '2.25rem' },
              }}
            >
              The Technology
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: { xs: 2.5, sm: 4 },
                fontSize: { xs: '0.95rem', sm: '1rem' },
              }}
            >
              {'REPLICO develops an AI-powered technology called VISIONERVES that enables the 3D '
                + 'modeling of a patient’s nervous system within its anatomical context. It has been '
                + 'specifically designed to assist surgeons, empowering them for:'}
            </Typography>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: { xs: 2, sm: 3 },
          width: '100%',
        }}
      >
        {items.map(({ icon, title, description }) => (
          <Card
            key={title}
            sx={(theme) => ({
              width: {
                xs: '100%',
                sm: 'calc(50% - 12px)',
                md: 'calc(33.333% - 16px)',
              },
              maxWidth: { xs: 420, sm: 'none' },
              mx: { xs: 'auto', sm: 0 },
              p: 0,
              borderRadius: { xs: '20px', sm: '5%' },
              border: 0,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: (theme.vars || theme).palette.action.hover,
                opacity: 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: 'none',
                zIndex: 0,
              },
              '&:hover::after': {
                opacity: 1,
              },
            })}
          >
            <CardActionArea
              disableRipple
              disableTouchRipple
              sx={{
                position: 'relative',
                zIndex: 1,
                '& .MuiCardActionArea-focusHighlight': {
                  opacity: 0,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {!loadedImages[title] && (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      height: { xs: 130, sm: 160, md: 190 },
                      width: '100%',
                    }}
                  />
                )}
                <CardMedia
                  component="img"
                  loading="lazy"
                  sx={{
                    height: { xs: 130, sm: 160, md: 190 },
                    width: '120%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    transform: 'scale(1.06)',
                    transformOrigin: 'center',
                    opacity: loadedImages[title] ? 1 : 0,
                    transition: 'opacity 200ms ease',
                  }}
                  image={icon}
                  alt={title}
                  onLoad={() => handleImageLoad(title)}
                />
                {!loadedImages[title] ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80%',
                    }}
                  >
                    <Skeleton variant="text" width="70%" height={32} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#333333',
                      textAlign: 'center',
                      width: '90%',
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.65rem' },
                        fontWeight: 410,
                        letterSpacing: '0.7px',
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                )}
              </Box>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                {!loadedImages[title] ? (
                  <>
                    <Skeleton variant="text" width="95%" height={18} />
                    <Skeleton variant="text" width="85%" height={18} />
                    <Skeleton variant="text" width="75%" height={18} />
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.84rem', sm: '0.9rem' },
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Container>
  );
}
